import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Truck, Send, Check, Trash2, ShieldCheck, MapPin } from "lucide-react";
import { toast } from "sonner";

const VEHICLES = ["Ambulance", "VSL", "TPMR", "Taxi"];
const todayStr = () => new Date().toISOString().slice(0, 10);

export function EmptyLegMarket({ canPublish = false }) {
  const [tribus, setTribus] = useState([]);
  const [legs, setLegs] = useState([]);
  const [requests, setRequests] = useState([]);

  const [offer, setOffer] = useState({ origin_address: "", dest_address: "", vehicle_type: "VSL", date: todayStr(), window_start: "14:00", window_end: "17:00", seats: 1, notes: "", tribu_id: "" });
  const [req, setReq] = useState({ tribu_id: "", pickup_address: "", dropoff_address: "", vehicle_type: "VSL", date: todayStr(), time: "15:00", patient_label: "" });

  const load = useCallback(() => {
    api.get("/tribus/mine").then((r) => setTribus(r.data)).catch(() => {});
    api.get("/transport-requests/mine").then((r) => setRequests(r.data)).catch(() => {});
    if (canPublish) api.get("/empty-legs/mine").then((r) => setLegs(r.data)).catch(() => {});
  }, [canPublish]);
  useEffect(() => { load(); }, [load]);

  const publishOffer = async () => {
    if (!offer.tribu_id) { toast.error("Choisissez une tribu cible"); return; }
    try {
      await api.post("/empty-legs", { ...offer, tribu_ids: [offer.tribu_id], seats: Number(offer.seats) });
      toast.success("Trajet à vide publié auprès de la tribu");
      setOffer((o) => ({ ...o, origin_address: "", dest_address: "", notes: "" }));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const deleteOffer = async (id) => { try { await api.delete(`/empty-legs/${id}`); load(); } catch (err) { toast.error(apiError(err)); } };

  const emitRequest = async () => {
    if (!req.tribu_id) { toast.error("Choisissez une tribu"); return; }
    try {
      const { data } = await api.post("/transport-requests", req);
      toast.success(`Demande émise — ${data.matches.length} correspondance(s) IA trouvée(s)`);
      setReq((r) => ({ ...r, pickup_address: "", dropoff_address: "", patient_label: "" }));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const acceptMatch = async (rid, legId) => {
    try {
      const { data } = await api.post(`/transport-requests/${rid}/accept`, { leg_id: legId });
      toast.success(`Attribué à ${data.transporter.company_name} (${data.transporter.email})`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="empty-leg-market">
      <p className="text-sm text-muted-foreground flex items-center gap-1">
        <ShieldCheck className="h-4 w-4 text-green-600" /> Matching IA · données anonymisées jusqu'à acceptation · zéro commission.
      </p>

      {canPublish && (
        <Card className="p-4 rounded-md shadow-none space-y-3" data-testid="publish-offer-card">
          <h3 className="font-display font-semibold flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> Publier un trajet à vide</h3>
          <div className="grid gap-3 md:grid-cols-3">
            <div><Label className="text-xs">Origine</Label><Input value={offer.origin_address} onChange={(e) => setOffer({ ...offer, origin_address: e.target.value })} placeholder="Paris" className="mt-1" data-testid="offer-origin" /></div>
            <div><Label className="text-xs">Destination</Label><Input value={offer.dest_address} onChange={(e) => setOffer({ ...offer, dest_address: e.target.value })} placeholder="Château-Chinon" className="mt-1" data-testid="offer-dest" /></div>
            <div>
              <Label className="text-xs">Véhicule</Label>
              <Select value={offer.vehicle_type} onValueChange={(v) => setOffer({ ...offer, vehicle_type: v })}>
                <SelectTrigger className="mt-1" data-testid="offer-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent>{VEHICLES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Date</Label><Input type="date" value={offer.date} onChange={(e) => setOffer({ ...offer, date: e.target.value })} className="mt-1" data-testid="offer-date" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">De</Label><Input type="time" value={offer.window_start} onChange={(e) => setOffer({ ...offer, window_start: e.target.value })} className="mt-1" data-testid="offer-window-start" /></div>
              <div><Label className="text-xs">À</Label><Input type="time" value={offer.window_end} onChange={(e) => setOffer({ ...offer, window_end: e.target.value })} className="mt-1" data-testid="offer-window-end" /></div>
            </div>
            <div>
              <Label className="text-xs">Tribu cible</Label>
              <Select value={offer.tribu_id} onValueChange={(v) => setOffer({ ...offer, tribu_id: v })}>
                <SelectTrigger className="mt-1" data-testid="offer-tribu"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>{tribus.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={publishOffer} className="gap-2" data-testid="offer-publish"><Send className="h-4 w-4" /> Publier l'annonce</Button>
        </Card>
      )}

      {canPublish && legs.length > 0 && (
        <div className="space-y-2" data-testid="my-offers">
          <h3 className="font-display font-semibold text-sm">Mes trajets à vide ({legs.length})</h3>
          {legs.map((l) => (
            <Card key={l.id} className="p-3 rounded-md shadow-none flex flex-wrap items-center justify-between gap-3" data-testid={`offer-${l.id}`}>
              <div>
                <p className="font-medium text-sm flex items-center gap-1"><MapPin className="h-3 w-3" />{l.origin_address} → {l.dest_address}</p>
                <p className="text-xs text-muted-foreground">{new Date(l.date).toLocaleDateString("fr-FR")} · {l.window_start}-{l.window_end} · {l.vehicle_type}</p>
                {l.status === "matched" && l.matched_request && (
                  <Badge variant="outline" className="mt-1 border-green-500 text-green-600">Attribué : {l.matched_request.requester_name} ({l.matched_request.time})</Badge>
                )}
              </div>
              {l.status === "open"
                ? <Button variant="ghost" size="icon" onClick={() => deleteOffer(l.id)} data-testid={`offer-delete-${l.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                : <Badge className="bg-green-600">Matché</Badge>}
            </Card>
          ))}
        </div>
      )}

      <Card className="p-4 rounded-md shadow-none space-y-3" data-testid="emit-request-card">
        <h3 className="font-display font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Émettre une demande de transport (matching IA)</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <div><Label className="text-xs">Prise en charge</Label><Input value={req.pickup_address} onChange={(e) => setReq({ ...req, pickup_address: e.target.value })} placeholder="Paris 15e" className="mt-1" data-testid="req-pickup" /></div>
          <div><Label className="text-xs">Dépose</Label><Input value={req.dropoff_address} onChange={(e) => setReq({ ...req, dropoff_address: e.target.value })} placeholder="Château-Chinon" className="mt-1" data-testid="req-dropoff" /></div>
          <div>
            <Label className="text-xs">Véhicule</Label>
            <Select value={req.vehicle_type} onValueChange={(v) => setReq({ ...req, vehicle_type: v })}>
              <SelectTrigger className="mt-1" data-testid="req-vehicle"><SelectValue /></SelectTrigger>
              <SelectContent>{VEHICLES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Date</Label><Input type="date" value={req.date} onChange={(e) => setReq({ ...req, date: e.target.value })} className="mt-1" data-testid="req-date" /></div>
          <div><Label className="text-xs">Heure</Label><Input type="time" value={req.time} onChange={(e) => setReq({ ...req, time: e.target.value })} className="mt-1" data-testid="req-time" /></div>
          <div>
            <Label className="text-xs">Tribu</Label>
            <Select value={req.tribu_id} onValueChange={(v) => setReq({ ...req, tribu_id: v })}>
              <SelectTrigger className="mt-1" data-testid="req-tribu"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{tribus.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={emitRequest} className="gap-2" data-testid="req-emit"><Sparkles className="h-4 w-4" /> Trouver une correspondance IA</Button>
      </Card>

      <div className="space-y-3" data-testid="my-requests">
        <h3 className="font-display font-semibold text-sm">Mes demandes ({requests.length})</h3>
        {requests.length === 0 && <Card className="p-6 rounded-md shadow-none text-center text-muted-foreground text-sm">Aucune demande émise.</Card>}
        {requests.map((r) => (
          <Card key={r.id} className="p-4 rounded-md shadow-none space-y-2" data-testid={`request-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-sm flex items-center gap-1"><MapPin className="h-3 w-3" />{r.pickup_address} → {r.dropoff_address}</p>
              <Badge variant={r.status === "matched" ? "default" : "outline"} className={r.status === "matched" ? "bg-green-600" : ""}>
                {r.status === "matched" ? "Attribuée" : `${r.matches.length} match IA`}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">{new Date(r.date).toLocaleDateString("fr-FR")} · {r.time} · {r.vehicle_type} · {r.tribu_name}</p>
            {r.matches.length === 0 && <p className="text-xs text-muted-foreground italic">Aucune correspondance pour l'instant — vous serez notifié dès qu'un trajet à vide compatible est publié.</p>}
            {r.matches.map((m) => (
              <div key={m.leg_id} className={`rounded-md border p-3 text-sm ${m.accepted ? "border-green-500 bg-green-500/5" : "border-border"}`} data-testid={`match-${m.leg_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold flex items-center gap-1"><Sparkles className="h-3 w-3 text-primary" /> Score IA {m.score}/100</span>
                  {m.accepted
                    ? <Badge className="bg-green-600">Accepté</Badge>
                    : (r.status !== "matched" && <Button size="sm" className="gap-1" onClick={() => acceptMatch(r.id, m.leg_id)} data-testid={`match-accept-${m.leg_id}`}><Check className="h-3 w-3" /> Accepter</Button>)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{m.origin} → {m.dest} · {m.vehicle_type} · {m.window}</p>
                <p className="text-xs mt-1">{m.reason}</p>
                <p className="text-xs text-muted-foreground mt-1">{m.accepted ? `${m.company_name} · ${m.company_email || ""}` : m.company_name}</p>
              </div>
            ))}
          </Card>
        ))}
      </div>
    </div>
  );
}
